import { EFinancingCompany } from '@if/core/types'
import { setApiClientAuthorization } from '@src/services/api-client'
import type { User, UserRight, UserRights } from '@src/data/types'
import { omit } from 'lodash-es'
import { createContext } from 'react'

export interface UserStore {
  user: User
}

interface UserDtoFromJwt {
  id: string
  username: string
  name: string
  roles: string
  rights: string
  companyIds: string
  langId: string
  isInternal: string
  exp: number
}

export function parseJwt(token: string): object {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join(''),
  )
  return JSON.parse(jsonPayload) as object
}

export function isExpiredToken(expiryDate: number): boolean {
  const date = new Date().valueOf() / 1000
  return date > expiryDate
}

function buildRights(jwtRights: UserRight[]): UserRights {
  const ret = {} as UserRights
  jwtRights.forEach((r: UserRight) => {
    ret[r] = true
  })
  return ret
}

export function loadUserFromJwtString(jwtToken: string | null): User | null {
  if (!jwtToken) return null
  const dto = parseJwt(jwtToken) as UserDtoFromJwt
  dto.companyIds ??= ''

  const user: User = {
    ...omit(dto, ['roles', 'rights']),
    isInternal: dto.isInternal === 'true',
    roles: dto.roles.split(','),
    rights: buildRights(dto.rights.split(',') as UserRight[]),
    companyIds: dto.companyIds.split(',') as EFinancingCompany[],
  }

  if (isExpiredToken(user.exp)) return null

  setApiClientAuthorization(jwtToken)
  return user
}

export const UserContext = createContext<UserStore | null>(null)

export const JWT_LOCAL_STORAGE_KEY = 'jwtToken'
export const CACHED_USER_TOKEN = localStorage.getItem(JWT_LOCAL_STORAGE_KEY)
